import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaSearch, FaChevronDown, FaChartLine, FaChevronUp, FaMapMarkedAlt, FaHandshake, FaDollarSign, FaLayerGroup, FaShieldAlt } from 'react-icons/fa';
import { resourceMap, ResourceUtils, searchResources, handlePDFGeneration, RESOURCE_TYPES } from '../utils/ResourceManager';
import Resources from '../components/Resources';
import StateRegulationsMap from '../components/StateRegulationsMap';
import '../styles/ImpactAcademy.css';

// Define mainCategories before using it
const mainCategories = [
  {
    id: 'fiscal_sponsorship',
    name: 'Fiscal Sponsorship',
    description: 'Comprehensive fiscal sponsorship programs and guidelines',
    icon: <FaHandshake />,
    resources: Object.values(resourceMap).filter(resource => 
      resource.tags?.some(tag => 
        ['Fiscal Sponsorship', 'Compliance', 'Risk Management', 'Partnerships', 'Growth'].includes(tag)
      )
    )
  },
  {
    id: 'program_types',
    name: 'Program Types',
    description: 'Specialized guides for different program types',
    icon: <FaLayerGroup />,
    resources: Object.values(resourceMap).filter(resource => 
      resource.tags?.some(tag => 
        ['Sports', 'Athletes', 'NIL', 'Environmental', 'Disaster', 'Documentary', 'Arts', 
         'Creative', 'Education', 'Youth', 'Community', 'Medical', 'Research'].includes(tag)
      )
    )
  },
  {
    id: 'fundraising',
    name: 'Fundraising & Development',
    description: 'Strategic fundraising and donor development resources',
    icon: <FaDollarSign />,
    resources: Object.values(resourceMap).filter(resource => 
      resource.tags?.some(tag => 
        ['Fundraising', 'Grants', 'Corporate Giving', 'CSR', 'Recurring Giving', 
         'Crowdfunding', 'Impact'].includes(tag)
      )
    )
  },
  {
    id: 'operations',
    name: 'Operations & Compliance',
    description: 'Essential operational and compliance resources',
    icon: <FaShieldAlt />,
    resources: Object.values(resourceMap).filter(resource => 
      resource.tags?.some(tag => 
        ['Operations', 'Compliance', 'Accounting', 'Financial', 'Technology', 'Innovation',
         'Leadership', 'Cause Management', 'Technology Integration'].includes(tag)
      )
    )
  },
  {
    id: 'innovative_charitable_frameworks',
    name: 'Innovative Charitable Frameworks',
    description: 'Advanced charitable structures and transformative giving solutions',
    icon: <FaChartLine />,
    resources: Object.values(resourceMap).filter(resource => 
      resource.tags?.some(tag => 
        ['Operational', 'Innovation', 'Impact', 'Risk Management', 'Innovative Giving', 'Innovation','Legacy Planning', 'Estate Planning', 'Tax Benefits', 'Charitable Remainder Funds', 'Charitable Giving', 'Corporate Giving', 'CSR', 'Donor Advised Funds', 'Recurring Giving', 'Cause Management', 'Corporate Giving', 'CSR', 'Donor Advised Funds', 'Recurring Giving'].includes(tag)
      )
    )
  }
];

// Get all available tags from ResourceUtils
const allTags = ResourceUtils.getResourceTags();

const ImpactAcademy = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [showStateMap, setShowStateMap] = useState(false);
  const [selectedState, setSelectedState] = useState(null);

  // Get all resources using the new structure
  const allResources = mainCategories.flatMap(category => category.resources);

  // Toggle category expansion and scroll to the clicked accordion
  const toggleCategory = (id) => {
    setExpandedCategory((prev) => {
      const newValue = prev === id ? null : id;
      
      // If we're expanding an accordion (not closing it)
      if (newValue) {
        // Wait for state to update and DOM to render
        setTimeout(() => {
          const element = document.getElementById(`accordion-${id}`);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
      }
      
      return newValue;
    });
  };

  // Enhanced search using the new searchResources function
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Filter resources using the new ResourceUtils
  const filteredResources = searchTerm 
    ? searchResources(searchTerm)
    : activeTag !== 'all'
    ? ResourceUtils.filterByTags([activeTag])
    : allResources;

  // Only show search results if there's a search term or active tag
  const showSearchResults = searchTerm !== '' || activeTag !== 'all';

  const handleStateClick = (state) => {
    setSelectedState(state);
  };

  const handleResourceClick = async (resource, e) => {
    e.preventDefault();
    console.log('handleResourceClick called with:', resource);
    
    try {
      // Map the resource to its correct type if needed
      const resourceType = RESOURCE_TYPES[resource.id?.toUpperCase()] || 
                          RESOURCE_TYPES[resource.title?.replace(/\s+/g, '_').toUpperCase()];
      
      // Create a properly formatted resource object
      const formattedResource = {
        ...resource,
        resourceType: resource.resourceType || resourceType
      };

      console.log('Formatted resource:', formattedResource); // Debug log

      if (formattedResource.generator) {
        const doc = await formattedResource.generator();
        if (doc) doc.save(`${formattedResource.id}-guide.pdf`);
      } else if (formattedResource.resourceType) {
        const doc = await handlePDFGeneration(formattedResource);
        if (doc) doc.save(`${formattedResource.title.replace(/\s+/g, '-').toLowerCase()}-guide.pdf`);
      } else if (formattedResource.onClick) {
        await formattedResource.onClick(e);
      } else {
        throw new Error(`No valid handler found for resource: ${formattedResource.title}`);
      }
    } catch (error) {
      console.error('Error handling resource click:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Impact Academy | Nonprofit Excellence Resource Center</title>
        <meta
          name="description"
          content="Comprehensive educational resources and expert guides for nonprofit leaders, fiscal sponsorship, and charitable program management."
        />
      </Helmet>

      <div className="academy-wrapper">
        <div className="academy-hero">
          <div className="hero-content">
            <h1>Impact Academy</h1>
            <p className="hero-subtitle">
              Your Gateway to Nonprofit Excellence & Fiscal Sponsorship Mastery
            </p>
            <div className="search-section">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search all resources..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="topic-tags">
              <button
                className={`category-button ${activeTag === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTag('all')}
              >
                {ResourceUtils.getCategoryIcon('EDUCATIONAL')}
                All Resources
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  className={`category-button ${activeTag === tag ? 'active' : ''}`}
                  onClick={() => setActiveTag(tag)}
                >
                  {ResourceUtils.getCategoryIcon(tag.toUpperCase())}
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="academy-container">
          {/* Search Results Section */}
          {showSearchResults && (
            <div className="search-results-section">
              <h2>Search Results for "{searchTerm}"</h2>
              {filteredResources.length > 0 ? (
                <Resources 
                  resources={filteredResources.map(resource => ({
                    ...resource,
                    onClick: (e) => handleResourceClick(resource, e)
                  }))} 
                />
              ) : (
                <div className="no-results">
                  <p>No resources found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
          )}

          <div className={`compliance-section ${selectedState ? 'state-selected' : ''}`}>
            <button 
              className="compliance-map-button"
              onClick={() => setShowStateMap(!showStateMap)}
            >
              <FaMapMarkedAlt />
              State Compliance Map
            </button>

            {showStateMap && (
              <div className="state-map-container">
                <StateRegulationsMap 
                  onStateClick={handleStateClick}
                  selectedState={selectedState}
                />
              </div>
            )}
          </div>

          <div className="educational-resources">
            <div className="accordion-container">
              {mainCategories.map((category) => (
                <div 
                  key={category.id} 
                  className="accordion-panel"
                  id={`accordion-${category.id}`}
                >
                  <div
                    className="panel-header"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="panel-header-content">
                      {category.icon}
                      <div>
                        <h2>{category.name}</h2>
                        <p className="category-description">{category.description}</p>
                      </div>
                    </div>
                    {expandedCategory === category.id ? (
                      <FaChevronUp className="icon" />
                    ) : (
                      <FaChevronDown className="icon" />
                    )}
                  </div>

                  {expandedCategory === category.id && (
                    <div className="panel-content">
                      <Resources
                        resources={category.resources.filter(resource =>
                          activeTag === 'all' || resource.tags?.includes(activeTag)
                        )}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImpactAcademy; 